import { useEffect, useState } from 'react';
import { formatCurrency } from '../../lib/format';
import { X, AlertTriangle, RotateCcw } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface InvoiceForRefund {
  _id: string;
  invoiceNumber: string;
  status: string;
  amountPaid: number;
  totalAmount: number;
  /** Cumulative total already refunded, if this invoice has been refunded before. */
  refundAmount?: number;
  paidAt?: string | Date;
  includedInPayoutId?: string;
  includedInSupervisorPayoutId?: string;
  physicianId?: string;
}

interface Props {
  invoice: InvoiceForRefund;
  onClose: () => void;
  onSuccess: () => void;
}

interface PharmacyPreview {
  pharmacyOrderCount: number;
  value: number;
  targets: Array<{ displayId: string; status: string; pharmacyOrderIds: string[]; value: number }>;
}

export function RefundInvoiceModal({ invoice, onClose, onSuccess }: Props) {
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'full' | 'partial'>('full');
  const [partialAmount, setPartialAmount] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // A refund is not a cancellation. Default to leaving the order alone: an order
  // still being fulfilled that reads as cancelled drops out of every fulfilment
  // report while the goods are in flight.
  const [orderDisposition, setOrderDisposition] = useState<'cancel' | 'keep_active'>('keep_active');
  // No default — when there are live fills this is the expensive decision, so it
  // is made explicitly rather than inherited from a pre-selected button.
  const [cancelAtPharmacy, setCancelAtPharmacy] = useState<boolean | null>(null);
  const [pharmacy, setPharmacy] = useState<PharmacyPreview | null>(null);

  useEffect(() => {
    let active = true;
    apiClient
      .invoicePharmacyCancellationPreview(invoice._id)
      .then(res => {
        if (active && res.success && res.data) setPharmacy(res.data as PharmacyPreview);
      })
      .catch(() => {
        // A failed preview must not block a refund; it just means we cannot offer
        // to stop the fill here, and the admin is told so below.
        if (active) setPharmacy(null);
      });
    return () => {
      active = false;
    };
  }, [invoice._id]);

  const amountPaid = invoice.amountPaid || invoice.totalAmount;
  // A second partial refund can only draw on what is left, not the original
  // amount paid — otherwise "Full" would try to return money twice.
  const alreadyRefunded = invoice.refundAmount || 0;
  const refundable = Math.round((amountPaid - alreadyRefunded) * 100) / 100;
  const partialAmountNum = parseFloat(partialAmount) || 0;
  const refundAmount = mode === 'full' ? refundable : partialAmountNum;

  const hasLiveFills = (pharmacy?.pharmacyOrderCount ?? 0) > 0;
  const partialInvalid =
    mode === 'partial' &&
    (partialAmountNum <= 0 || partialAmountNum > refundable + 0.001);
  const reasonInvalid = reason.trim().length < 5 || reason.trim().length > 500;
  const pharmacyUnanswered = hasLiveFills && cancelAtPharmacy === null;
  const submitDisabled = submitting || partialInvalid || reasonInvalid || pharmacyUnanswered;

  const willClawback = !!(invoice.includedInPayoutId || invoice.includedInSupervisorPayoutId);

  const handleSubmit = async () => {
    if (reasonInvalid) {
      toast.error('Reason must be 5–500 characters');
      return;
    }
    if (partialInvalid) {
      toast.error(`Amount must be between $0.01 and ${formatCurrency(refundable)}`);
      return;
    }
    if (pharmacyUnanswered) {
      toast.error('Choose whether to stop the pharmacy fills for this invoice');
      return;
    }

    setSubmitting(true);
    try {
      const body: {
        reason: string;
        amount?: number;
        orderDisposition: 'cancel' | 'keep_active';
        cancelAtPharmacy?: boolean;
      } = {
        reason: reason.trim(),
        orderDisposition,
        cancelAtPharmacy: cancelAtPharmacy === true
      };
      if (mode === 'partial') body.amount = partialAmountNum;

      const res = await apiClient.refundInvoice(invoice._id, body);

      if (res.success) {
        toast.success(
          mode === 'full'
            ? `Refunded ${formatCurrency(refundAmount)} on invoice ${invoice.invoiceNumber}`
            : `Refunded ${formatCurrency(refundAmount)} (partial) on invoice ${invoice.invoiceNumber}`
        );
        onSuccess();
        onClose();
      } else {
        const msg =
          res.code === 'refund_not_authorized'
            ? 'Refund permissions not enabled for your account.'
            : res.code === 'processor_refund_failed'
            ? 'Payment processor rejected the refund. Check the audit log for details.'
            : res.code === 'no_payment_intent'
            ? 'Invoice has no recorded payment intent — cannot refund through the processor.'
            : res.error || 'Refund failed';
        toast.error(msg);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.code === 'refund_not_authorized'
          ? 'Refund permissions not enabled for your account.'
          : data?.code === 'processor_refund_failed'
          ? 'Payment processor rejected the refund.'
          : data?.error || err?.message || 'Refund failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]"
      onClick={onClose}
    >
      <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-medium text-gray-900">
              Refund invoice {invoice.invoiceNumber}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-md p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-600">Amount paid</span>
              <span className="font-medium">{formatCurrency(amountPaid)}</span>
            </div>
            {invoice.paidAt && (
              <div className="flex justify-between">
                <span className="text-gray-600">Paid on</span>
                <span>{new Date(invoice.paidAt).toLocaleDateString()}</span>
              </div>
            )}
            {alreadyRefunded > 0 && (
              <>
                <div className="flex justify-between text-amber-800">
                  <span>Already refunded</span>
                  <span className="font-medium">−{formatCurrency(alreadyRefunded)}</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                  <span className="text-gray-600">Still refundable</span>
                  <span className="font-medium">{formatCurrency(refundable)}</span>
                </div>
              </>
            )}
          </div>

          {willClawback && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <div className="font-medium">Already paid out</div>
                <div className="text-amber-800">
                  This invoice has been included in a paid-out cycle. Refunding will issue a
                  credit memo so the clawback amount is deducted from the next AM
                  {invoice.includedInSupervisorPayoutId ? ' (and supervisor)' : ''} payout.
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Refund amount</label>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setMode('full')}
                className={`px-3 py-1.5 text-sm rounded border ${
                  mode === 'full'
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Full ({formatCurrency(refundable)})
              </button>
              <button
                type="button"
                onClick={() => setMode('partial')}
                className={`px-3 py-1.5 text-sm rounded border ${
                  mode === 'partial'
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Partial
              </button>
            </div>
            {mode === 'partial' && (
              <div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={refundable}
                    value={partialAmount}
                    onChange={e => setPartialAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                {partialInvalid && partialAmount !== '' && (
                  <p className="text-xs text-red-600 mt-1">
                    Must be between $0.01 and {formatCurrency(refundable)}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="What's the reason for this refund? (visible in the audit log)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-gray-500 mt-1">{reason.trim().length} / 500 (min 5)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              This order in Bloom
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="orderDisposition"
                  checked={orderDisposition === 'keep_active'}
                  onChange={() => setOrderDisposition('keep_active')}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-900">Leave active</span>
                  <span className="block text-xs text-gray-500">
                    The order is still being fulfilled — a price adjustment or goodwill refund.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="orderDisposition"
                  checked={orderDisposition === 'cancel'}
                  onChange={() => setOrderDisposition('cancel')}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-900">Cancel it</span>
                  <span className="block text-xs text-gray-500">
                    The order should not have been placed, or is being unwound.
                  </span>
                </span>
              </label>
            </div>
          </div>

          {hasLiveFills && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-900">
                  <div className="font-medium">
                    {pharmacy!.pharmacyOrderCount} live pharmacy{' '}
                    {pharmacy!.pharmacyOrderCount === 1 ? 'order' : 'orders'} (
                    {formatCurrency(pharmacy!.value)})
                  </div>
                  <div className="text-red-800 text-xs mt-0.5">
                    Refunding does not stop these. Unless cancelled at the terminal, the
                    pharmacy will fill and ship them.
                    {pharmacy!.targets.length > 0 && (
                      <span className="block mt-1">
                        {pharmacy!.targets
                          .map(t => `${t.displayId} (${t.pharmacyOrderIds.length})`)
                          .join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setCancelAtPharmacy(true)}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    cancelAtPharmacy === true
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Cancel them too
                </button>
                <button
                  type="button"
                  onClick={() => setCancelAtPharmacy(false)}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    cancelAtPharmacy === false
                      ? 'bg-gray-700 text-white border-gray-700'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Leave them to ship
                </button>
              </div>
              {cancelAtPharmacy === null && (
                <p className="text-xs text-red-700 mt-2">Pick one to continue.</p>
              )}
            </div>
          )}

          <div className="bg-orange-50 border border-orange-200 rounded-md p-3 text-sm">
            <div className="flex justify-between font-medium text-orange-900">
              <span>To be refunded</span>
              <span>{formatCurrency(refundAmount || 0)}</span>
            </div>
            <p className="text-xs text-orange-700 mt-1">
              The refund will be sent through the payment processor and cannot be reversed automatically.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Refunding…' : `Refund ${formatCurrency(refundAmount || 0)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
